import type {
  CertificationAndLicense,
  Resource,
  ResourceHistoryFieldChange,
} from '@dispatch/shared/types';

/**
 * The audit payload identifies a collection-membership change by the entity's
 * GUID embedded in the field path — e.g. `Offices.[<integrationGUID>]` with
 * `newValue: "created" | "deleted"`. On its own that reads as an anonymous
 * "Office — created" row; resolving the GUID to the entity's display name
 * tells the reader *which* office/team/group/role was added or removed.
 */

/**
 * The resource/company collections whose membership the audit report tracks,
 * spelled exactly as `AuditReportBuilder` emits the field-path segment. Typing
 * the wiring against this union turns a mis-spelled key into a compile error
 * rather than a silently empty index (whose only symptom is the fallback).
 */
export type MembershipCollection =
  | 'Offices'
  | 'Teams'
  | 'Groups'
  | 'Roles'
  | 'SelectedAttributeDefinitions'
  | 'SelectedAttributeGroups'
  | 'Locations'
  | 'SelectedCertificationsAndLicenses'
  // Nested attribute *values* live under a definition
  // (`SelectedAttributeDefinitions.[a].SelectedAttributeValuesVarchars.[b]`);
  // resolving the leaf tells the reader which value was added/removed.
  | 'SelectedAttributeValuesVarchars'
  | 'SelectedAttributeValuesInts'
  | 'SelectedAttributeValuesFloats'
  | 'SelectedAttributeValuesBools';

/**
 * Every collection listed once, as a `Record` keyed on the union so the compiler
 * rejects an omission: adding a member to `MembershipCollection` without adding
 * it here is a type error naming the missing key.
 */
const ALL_MEMBERSHIP_COLLECTIONS: Record<MembershipCollection, true> = {
  Offices: true,
  Teams: true,
  Groups: true,
  Roles: true,
  SelectedAttributeDefinitions: true,
  SelectedAttributeGroups: true,
  Locations: true,
  SelectedCertificationsAndLicenses: true,
  SelectedAttributeValuesVarchars: true,
  SelectedAttributeValuesInts: true,
  SelectedAttributeValuesFloats: true,
  SelectedAttributeValuesBools: true,
};

/**
 * The union, enumerable at runtime so a test can assert the wiring actually
 * supplies (and resolves) every collection. The type alone cannot catch a
 * collection that is passed in but whose entities all lack a name: the index
 * builds empty and the only symptom is a silent fallback to the bare verb.
 */
export const MEMBERSHIP_COLLECTIONS = Object.keys(
  ALL_MEMBERSHIP_COLLECTIONS
) as readonly MembershipCollection[];

/** Resolves a member GUID within a named collection to its display name. */
export type MemberNameResolver = (
  collection: string,
  memberId: string
) => string | undefined;

/** Rendered `Old`/`New` cell text for one field change. */
export type ChangeCells = {
  oldValue: string;
  newValue: string;
};

/**
 * Minimal shape shared by the resolvable collections. Exactly one of
 * `integrationGUID` / `id` is REQUIRED — one of them is the lookup key, and
 * making both optional would let TypeScript's weak-type check pass a collection
 * that renamed the field (e.g. to `guid`), yielding a silently empty index.
 *
 * Most collections key on `integrationGUID`, but `SelectedAttributeGroups` has
 * no such field: the audit report keys it on `Id` (see AuditReportBuilder,
 * `x => x!.Id`) and the resource payload spells it `id`. Hence `integrationGUID
 * ?? id`. `id` is `string | number` because the payload types disagree — the
 * key is stringified before indexing.
 *
 * Offices/Teams/Groups/CompanyRole expose `name`; attribute selections expose
 * `displayName` — either works.
 */
type IdentifiableEntity = {
  name?: string;
  displayName?: string;
} & (
  | { integrationGUID: string; id?: string | number }
  | { integrationGUID?: undefined; id: string | number }
);

const EMPTY_CELL = '—';

// A membership add/remove row ends with `<Collection>.[<id>]`. The match is
// anchored to the END so a nested diff resolves its LEAF — e.g.
// `SelectedAttributeDefinitions.[a].SelectedAttributeValuesVarchars.[b]` captures
// `SelectedAttributeValuesVarchars` + `b` (which value changed), while a plain
// `Offices.[guid]` captures `Offices` + `guid`. A row ending in a scalar segment
// (`….[a].DisplayName`) does not match and keeps its raw old→new values. Unwired
// collections (e.g. `Companies`) still fall through to the raw verb.
const MEMBERSHIP_PATH = /(?:^|\.)([A-Za-z]+)\.\[([^\]]+)\]$/;

/**
 * The raw audit verbs for a collection-membership diff, matched verbatim. The
 * bare lowercase "created"/"deleted" tokens are minted by `AuditReportParser`
 * (SharedAssemblies), which normalizes `AuditReportBuilder`'s full sentence
 * ("a collection item was created", ADO #86965) down to `FieldChange.newValue`.
 * We surface them as-is rather than re-labeling (e.g. to "added"/"removed"): if
 * the wording should change, that belongs at the parser/builder source under
 * its own ticket, not as a display-layer guess. NOTE: this set is the third hop
 * of a string contract (builder → parser → here) — keep it in sync with the
 * parser's literals.
 */
const MEMBERSHIP_VERBS = new Set(['created', 'deleted']);

/**
 * Builds a resolver over the resource/company collections. Each entity is
 * indexed by its `integrationGUID` — the identifier the audit report embeds in
 * the field path for every collection (see AuditReportBuilder, keyed uniformly
 * on IntegrationGuid). An entity no longer present (e.g. a since-deleted office)
 * simply won't resolve.
 *
 * GUIDs are lower-cased on both write and read: the audit path is a .NET
 * `Guid.ToString()` (always lowercase) while the company payload's
 * `integrationGUID` is an unnormalized JSON string. `ResourcesList` performs
 * this same resource↔company join and `.toLowerCase()`s both sides for exactly
 * this reason — a casing mismatch here would silently fall back to the bare
 * verb, indistinguishable from a legitimately-deleted member.
 */
export function buildMemberNameIndex(
  collections: Partial<
    Record<MembershipCollection, readonly IdentifiableEntity[] | undefined>
  >
): MemberNameResolver {
  const indexes = new Map<string, Map<string, string>>();

  for (const [collection, entities] of Object.entries(collections)) {
    const index = new Map<string, string>();
    for (const entity of entities ?? []) {
      const name = entity.name ?? entity.displayName;
      const key = entity.integrationGUID ?? entity.id;
      if (name && key !== undefined && key !== '') {
        index.set(String(key).toLowerCase(), name);
      }
    }
    indexes.set(collection, index);
  }

  return (collection, memberId) =>
    indexes.get(collection)?.get(memberId.toLowerCase());
}

/**
 * Maps a resource (plus the company certification catalog) onto the collections
 * whose membership the audit report tracks.
 *
 * Extracted from `ResourceHistoryTab` so the wiring is assertable: the tab built
 * this object inline, where a missing or silently-empty collection had no symptom
 * other than membership rows degrading to a bare "created"/"deleted".
 */
export function buildMemberCollections(
  resource: Resource | undefined,
  // The catalog as an index rather than a list: the caller already needs the same
  // index to name a REMOVED certification off the audit entry, so taking it here
  // means it is built once instead of once per consumer.
  certificationNameByCatalogGuid: Map<string, string>
): Record<MembershipCollection, readonly IdentifiableEntity[] | undefined> {
  // Attribute *values* live nested under each definition, so a nested audit row
  // (`SelectedAttributeDefinitions.[a].SelectedAttributeValuesVarchars.[b]`)
  // resolves against them. Flatten across every definition into one index per
  // value type so "which value" was added/removed shows its display name.
  const definitions = resource?.selectedAttributeDefinitions ?? [];

  return {
    Offices: resource?.company?.office,
    Teams: resource?.company?.team,
    Groups: resource?.company?.group,
    Roles: resource?.company?.role,
    SelectedAttributeDefinitions: resource?.selectedAttributeDefinitions,
    SelectedAttributeGroups: resource?.selectedAttributeGroups,
    Locations: resource?.locations,
    SelectedCertificationsAndLicenses: resolveCertificationNames(
      resource?.selectedCertificationsAndLicenses,
      certificationNameByCatalogGuid
    ),
    SelectedAttributeValuesVarchars: definitions.flatMap(
      (definition) => definition.selectedAttributeValuesVarchars ?? []
    ),
    SelectedAttributeValuesInts: definitions.flatMap(
      (definition) => definition.selectedAttributeValuesInts ?? []
    ),
    SelectedAttributeValuesFloats: definitions.flatMap(
      (definition) => definition.selectedAttributeValuesFloats ?? []
    ),
    SelectedAttributeValuesBools: definitions.flatMap(
      (definition) => definition.selectedAttributeValuesBools ?? []
    ),
  };
}

/**
 * Builds `{ integrationGUID: <resource row id>, name: <catalog name> }` for each
 * assigned certification.
 *
 * A certification is the one collection whose name is NOT in the resource payload
 * at all: the entry carries only `certificationAndLicenseIntegrationGUID`, a key
 * into the company catalog. So this is a TWO-hop join — the audit GUID (the
 * resource row id, which is what the audit field path embeds) → that row's
 * catalog GUID → the catalog's `name`. Mirrors the join `CertsAndLicensesTab`
 * already performs to title the certs grid.
 *
 * A row with no catalog match yields `name: undefined` and simply won't resolve,
 * which degrades to the bare verb rather than rendering blank.
 */
function resolveCertificationNames(
  assigned: Resource['selectedCertificationsAndLicenses'] | undefined,
  nameByCatalogGuid: Map<string, string>
): readonly IdentifiableEntity[] {
  return (assigned ?? []).map((row) => ({
    integrationGUID: row.integrationGUID,
    name: nameByCatalogGuid.get(
      (row.certificationAndLicenseIntegrationGUID ?? '').toLowerCase()
    ),
  }));
}

/**
 * Indexes the company certification catalog by `integrationGUID`, lower-cased for
 * the same reason `buildMemberNameIndex` lower-cases: the audit path is a .NET
 * `Guid.ToString()` (always lowercase) while the payload GUID is an unnormalized
 * JSON string.
 */
export function buildCertificationCatalogIndex(
  catalog: readonly CertificationAndLicense[] | undefined
): Map<string, string> {
  const nameByGuid = new Map<string, string>();

  for (const entry of catalog ?? []) {
    if (entry.integrationGUID && entry.name) {
      nameByGuid.set(entry.integrationGUID.toLowerCase(), entry.name);
    }
  }

  return nameByGuid;
}

/**
 * Produces the `Old`/`New` cell text for a field change. A collection
 * membership add/remove (`<Collection>.[<id>]` with a created/deleted verb)
 * shows the member's resolved name alongside the raw audit verb (e.g.
 * "Downtown Branch (created)"), or the bare verb when the name can't be
 * resolved. The name is placed to respect the "Old → New" direction: a removal
 * goes in `Old` (it was there, now gone), an addition in `New` — the audit
 * always sends `oldValue: null`, so the column is the display layer's to pick,
 * and getting it wrong makes a deletion read as an addition. Every other change
 * keeps its raw audit values (scalar edits already read as old → new).
 */
export function formatChangeCells(
  change: ResourceHistoryFieldChange,
  resolveName: MemberNameResolver
): ChangeCells {
  const match = MEMBERSHIP_PATH.exec(change.fieldPath);
  const verb = change.newValue;
  if (match && verb && MEMBERSHIP_VERBS.has(verb)) {
    const name = resolveName(match[1], match[2]);
    const label = name ? `${name} (${verb})` : verb;
    return verb === 'deleted'
      ? { oldValue: label, newValue: EMPTY_CELL }
      : { oldValue: EMPTY_CELL, newValue: label };
  }

  // `||` not `??`: an added/removed value transition arrives as an empty string
  // (AuditReportParser yields "" for the empty side, never null), so `??` would
  // render a blank cell where every other unset cell in the grid shows the em dash.
  return {
    oldValue: change.oldValue || EMPTY_CELL,
    newValue: change.newValue || EMPTY_CELL,
  };
}
